// @oasis-permission-inherits: /ai-edit-tool (ai-edit-v2-tool:view)
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { ErrorText } from "~/modules/ai-edit-tool/components/error-text";
import { Loading } from "~/modules/ai-edit-tool/components/loading";
import { useStyleEditorPermissions } from "~/modules/ai-edit-tool/hooks/use-style-editor-permissions";
import { getAiEditStyles } from "~/modules/ai-edit-tool/network/queries";
import { VideoStyleDetailPanel } from "~/routes/_dash/ai-edit-tool/-components/video-style-detail-panel";
import { VideoStylesList } from "~/routes/_dash/ai-edit-tool/-components/video-styles-list";
import { usePreviewOptimization } from "~/routes/_dash/ai-edit-tool/-hooks/use-preview-optimization";
import { searchParamsSchema } from "~/routes/_dash/ai-edit-tool/-lib/search-params";

export const Route = createFileRoute("/_dash/ai-edit-tool/")({
  component: RouteComponent,
  validateSearch: searchParamsSchema,
});

function RouteComponent() {
  const { canEdit, canEditStyle } = useStyleEditorPermissions();
  const { videoStyleId: initialVideoStyleId, tab: initialTab } = Route.useSearch();

  const {
    data: videoStylesData,
    isLoading: isVideoStylesLoading,
    isError: isVideoStylesError,
    error: videoStylesError,
  } = getAiEditStyles.useQuery({
    params: {
      client: "DESKTOP",
      version: "dev",
      afterCursor: "",
      tags: [],
      pageSize: 300,
    },
  });

  const [selectedStyleId, setSelectedStyleId] = useState<string | null>(
    initialVideoStyleId ?? null
  );

  const { previewOptimizationStates, submitPreviewOptimization } = usePreviewOptimization();

  const selectedStyle = selectedStyleId
    ? videoStylesData?.styles?.page.find((style) => style.id === selectedStyleId)
    : undefined;
  const canEditSelectedStyle = selectedStyleId ? canEditStyle(selectedStyleId) : false;
  const selectedPreviewOptimizationState = selectedStyleId
    ? previewOptimizationStates[selectedStyleId]
    : undefined;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {!videoStylesData && (
        <div className="flex gap-4 items-center">
          <h1 className="text-2xl font-semibold tracking-tight">Video styles</h1>
        </div>
      )}
      {isVideoStylesLoading && <Loading />}
      {isVideoStylesError && <ErrorText>{videoStylesError.message}</ErrorText>}

      {videoStylesData && (
        <div className="flex flex-col xl:flex-row gap-6 min-w-0 select-text items-start">
          <VideoStylesList
            styles={videoStylesData.styles?.page ?? []}
            selectedStyleId={selectedStyleId}
            onSelectStyle={(id) => setSelectedStyleId((current) => (current === id ? null : id))}
            canEdit={canEdit}
            canEditStyle={canEditStyle}
          />
          {selectedStyleId && (
            <VideoStyleDetailPanel
              selectedStyleId={selectedStyleId}
              selectedStyle={selectedStyle}
              canEditSelectedStyle={canEditSelectedStyle}
              initialTab={initialTab}
              previewOptimizationState={selectedPreviewOptimizationState}
              submitPreviewOptimization={submitPreviewOptimization}
            />
          )}
        </div>
      )}
    </div>
  );
}
